import React, { useState, useEffect, useMemo } from "react";
import { MDBDataTable } from "mdbreact";
import { Row, Col, Card, CardBody, CardTitle, Button, Alert, UncontrolledAlert } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { del } from "../../Services/RestAPIService";
import { fetchAllPages } from "../../common/tableData";
import Dummy from "../../assets/images/No_Image_Available.jpg";
import { useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import AlertModal from "components/AlertModal";

const DatatableTables = ({ t }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isAlertModal, setIsAlertModal] = useState(false);
  const [deleteUserId, setdeleteUserId] = useState(false);
  const [isLoadingDel, setIsLoadingDel] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  
  const getUsersData = async () => {
    setIsLoading(true);
    setLoadError("");

    try {
      const loadedUsers = await fetchAllPages(
        (page, pageSize) =>
          `v1/users/?user_type=Patient&page=${page}&page_size=${pageSize}`
      );
      if (cancelled) return;
      setUsers(loadedUsers);
    } catch (error) {
      if (!cancelled) {
        setLoadError(t("Unable to load users. Please try again."));
      }
      console.error("Error fetching user data:", error);
    } finally {
      if (!cancelled) setIsLoading(false);
    }
  };

  useEffect(() => {
    setCancelled(false);
    getUsersData();
    return () => {
      setCancelled(true);
    };
  }, []);

  const handleEdit = (id) => {
    navigate(`/users/${id}`);
  };

  const [alert, setAlert] = useState({
    color: "",
    message: "",
    visible: false,
  });

  const handleDeleteClick = (id) => {
    setIsAlertModal(true);
    setdeleteUserId(id);
  };

  const handleDelete = async () => {
    setIsLoadingDel(true);
    setIsAlertModal(false);

    try {
      const response = await del(`v1/users/${deleteUserId}/`);
      if (response.status <= 400) {
        setAlert({
          color: "success",
          visible: true,
          message: t("User deleted successfully!"),
        });
        setCancelled(false);
        await getUsersData();
        return;
      }
      const errorMessage =
        response.data?.error || t("An error occurred while deleting user.");
      setAlert({
        color: "danger",
        visible: true,
        message: errorMessage,
      });
      setTimeout(() => {
        setAlert({ visible: false });
      }, 3000);
    } catch (error) {
      console.error("Error deleting user:", error);
      setAlert({
        color: "danger",
        visible: true,
        message: t("Error Deleting User"),
      });
      setTimeout(() => {
        setAlert({ visible: false });
      }, 3000);
    } finally {
      setIsLoadingDel(false);
      setTimeout(() => {
        setAlert({ visible: false });
      }, 1500);
    }
  };

  const tableData = useMemo(
    () => ({
      columns: [
        {
          label: t("ID"),
          field: "id",
          sort: "asc",
          width: 100,
        },
        {
          label: t("Image"),
          field: "image",
          sort: "disabled",
          searchable: false,
          width: 100,
        },
        {
          label: t("Name"),
          field: "name",
          sort: "asc",
          width: 180,
        },
        {
          label: t("Email"),
          field: "email",
          sort: "asc",
          width: 220,
        },
        {
          label: t("Gender"),
          field: "gender",
          sort: "asc",
          width: 120,
        },
        {
          label: t("Activation Status"),
          field: "status",
          sort: "asc",
          width: 150,
        },
        {
          label: t("Action"),
          field: "edit",
          sort: "disabled",
          searchable: false,
          width: 200,
        },
      ],
      rows: users.map((user) => ({
        id: user.id,
        image: (
          <img
            src={user.profile_image || Dummy}
            alt="User"
            width="50"
            height="50"
          />
        ),
        name:
          [user.first_name, user.last_name].filter(Boolean).join(" ") || "-",
        email: user.email || "-",
        gender: user.gender ? t(user.gender) : "-",
        status: user.status ? t("Active") : t("Inactive"),
        edit: (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              gap: "5px",
            }}
          >
            <Button color="primary" onClick={() => handleEdit(user.id)}>
              {t("Edit")}
            </Button>

            <Button color="danger" onClick={() => handleDeleteClick(user.id)}>
              {t("Delete")}
            </Button>
          </div>
        ),
      })),
    }),
    [users, t]
  );

  const paginationOptions = {
    entries: 10,
    entriesOptions: [10, 25, 50, 100],
    responsive: true,
    striped: true,
    bordered: false,
    info: true,
    infoLabel: [t("Showing"), t("to"), t("of"), t("entries")],
    paginationLabel: ["<", ">"],
    noRecordsFoundLabel: t(isLoading ? "Loading..." : "No records found"),
    searchLabel: t("Search"),
  };

  document.title = "Data Tables | Llosana";

  return (
    <React.Fragment>
      {alert.visible && (
        <UncontrolledAlert className="Alert_Additions_Log" color={alert.color}>
          {alert.message}
        </UncontrolledAlert>
      )}
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            maintitle={t("Home")}
            title={t("Data")}
            breadcrumbItem={t("Users")}
          />
          <Row>
            {isAlertModal && (
              <AlertModal
                selection={(e) => {
                  e === true ? handleDelete() : setIsAlertModal(false);
                }}
                Display={null}
              />
            )}

            <Col className="col-12">
              <Card>
                <CardBody>
                  <CardTitle className="h4">{t("Users Listing")}</CardTitle>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-end",
                      marginBottom: "15px",
                    }}
                  >
                    <Button
                      color="primary"
                      onClick={() => navigate(`/users/create-user`)}
                    >
                      <i className="typcn typcn-plus"></i>
                      {t("Create New User")}
                    </Button>
                  </div>
                  {loadError ? (
                    <Alert color="danger" className="mb-3">
                      {loadError}
                    </Alert>
                  ) : null}

                  <MDBDataTable
                    exportToCSV={true}
                    data={tableData}
                    order={["id", "asc"]}
                    responsive={paginationOptions.responsive}
                    striped={paginationOptions.striped}
                    bordered={paginationOptions.bordered}
                    searching={true}
                    sortable={true}
                    searchLabel={paginationOptions.searchLabel}
                    info={paginationOptions.info}
                    infoLabel={paginationOptions.infoLabel}
                    paging={true}
                    paginationLabel={paginationOptions.paginationLabel}
                    entries={paginationOptions.entries}
                    entriesOptions={paginationOptions.entriesOptions}
                    displayEntries={true}
                    noRecordsFoundLabel={paginationOptions.noRecordsFoundLabel}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(DatatableTables);
